import { useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Breadcrumb,
  BreadcrumbItem,
} from "reactstrap";
import { getAllCertifications } from "../../services/certifications";
import { useTranslation } from "react-i18next";
import "./certifications.css";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { assignCertificateToTenant } from "../../services/certifications";
import LoadingButton from "@/components/Buttons/LoadingButton";

export default function CertificateSigningApp() {
  const { id } = useParams();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const previewWrapRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string>("");
  const [signaturePosition, setSignaturePosition] = useState<{
    x: number;
    y: number;
  }>({ x: 100, y: 100 });
  const [signatureSize, setSignatureSize] = useState<number>(100);
  const [rotation, setRotation] = useState<number>(0);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });
  const [displayedImageDimensions, setDisplayedImageDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });
  const [fontColor, setFontColor] = useState<string>("#000000");
  const [fontSize, setFontSize] = useState<number>(24);
  const [certificates, setCertificates] = useState<
    { id: number; name: string; src: string }[]
  >([]);
  const [selectedCertificate, setSelectedCertificate] = useState<string | null>(
    null
  );
  const [datePosition, setDatePosition] = useState<{ x: number; y: number }>({
    x: 50,
    y: 50,
  });
  const [dateSize, setDateSize] = useState<number>(24);
  const { t } = useTranslation();
  const [selectedCertificateId, setSelectedCertificateId] = useState<
    number | undefined
  >();

  const getCertificates = async () => {
    try {
      const response = await getAllCertifications("", 100, 0);
      if (response.success) {
        setCertificates(response.data);
      }
    } catch (error) {
      console.error("Error fetching certificates:", error);
    }
  };

  useEffect(() => {
    getCertificates();
  }, []);

  useEffect(() => {
    const el = previewWrapRef.current;
    if (!uploadedImage || !el) return;

    const syncDisplayed = () => {
      const img = el.querySelector("img");
      if (img && img.clientWidth > 0) {
        setDisplayedImageDimensions({
          width: img.clientWidth,
          height: img.clientHeight,
        });
      }
    };

    syncDisplayed();
    const ro = new ResizeObserver(() => syncDisplayed());
    ro.observe(el);
    return () => ro.disconnect();
  }, [uploadedImage]);

  const handleCertificateSelect = (imageUrl: string, certificateId: number) => {
    const fullImageUrl =
      process.env.REACT_APP_API_URL + imageUrl + "?public=true";
    setSelectedCertificate(fullImageUrl);
    setUploadedImage(fullImageUrl);
    setSelectedCertificateId(certificateId);
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    (canvas as unknown as { isDrawing?: boolean }).isDrawing = true;
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas || !(canvas as unknown as { isDrawing?: boolean }).isDrawing)
      return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    ctx.strokeStyle = fontColor;
    ctx.lineWidth = fontSize / 4;
    ctx.stroke();
  };

  const stopDrawing = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      (canvas as unknown as { isDrawing?: boolean }).isDrawing = false;
    }
  };

  const clearCanvas = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureDataUrl("");
  };

  const saveSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const url = canvas.toDataURL();
    setSignatureDataUrl(url);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setDragImage(new Image(), 0, 0);
    isDraggingRef.current = true;
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>, type: string) => {
    if (!isDraggingRef.current) return;
    const rect = e.currentTarget.parentElement?.getBoundingClientRect();
    if (rect) {
      let x = e.clientX - rect.left;
      let y = e.clientY - rect.top;

      x = Math.max(0, Math.min(x, displayedImageDimensions.width));
      y = Math.max(0, Math.min(y, displayedImageDimensions.height));

      if (type === "signature") {
        setSignaturePosition({ x, y });
      } else if (type === "date") {
        setDatePosition({ x, y });
      }
    }
  };

  const handleDragEnd = () => {
    isDraggingRef.current = false;
  };

  const normalizePosition = (x: number, y: number, type?: string) => {
    const scaleX = imageDimensions.width / displayedImageDimensions.width;
    const scaleY = imageDimensions.height / displayedImageDimensions.height;
    const verticalOffset = 35;

    const xOffset = type === "date" ? 160 : 0;

    return {
      x: x * scaleX + xOffset,
      y: y * scaleY + verticalOffset,
    };
  };

  const saveImageWithSignatureAsync = async (): Promise<void> => {
    return new Promise<void>((resolve) => {
      const canvas = canvasRef.current;
      if (!canvas || !uploadedImage) {
        console.error("Canvas or uploaded image is missing");
        resolve();
        return;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        console.error("Canvas context is missing");
        resolve();
        return;
      }

      const backgroundImg = new Image();
      backgroundImg.src = uploadedImage;
      backgroundImg.crossOrigin = "anonymous";

      const drawImageWithSignature = new Promise<void>((resolveDraw, rejectDraw) => {
        backgroundImg.onload = () => {
          canvas.width = imageDimensions.width;
          canvas.height = imageDimensions.height;

          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(backgroundImg, 0, 0, canvas.width, canvas.height);

          if (signatureDataUrl) {
            const signatureImg = new Image();
            signatureImg.src = signatureDataUrl;
            signatureImg.onload = () => {
              ctx.save();

              const normalizedPos = normalizePosition(
                signaturePosition.x,
                signaturePosition.y
              );

              ctx.translate(normalizedPos.x, normalizedPos.y);
              ctx.rotate((rotation * Math.PI) / 180);

              ctx.drawImage(
                signatureImg,
                -signatureSize / 2,
                -signatureSize / 2,
                signatureSize,
                (signatureImg.height / signatureImg.width) * signatureSize
              );

              ctx.restore();
              resolveDraw();
            };
            signatureImg.onerror = (error) => {
              console.error("Error loading signature image:", error);
              rejectDraw(error);
            };
          } else {
            resolveDraw();
          }
        };
        backgroundImg.onerror = (error) => {
          console.error("Error loading background image:", error);
          rejectDraw(error);
        };
      });

      drawImageWithSignature
        .then(() => {
          ctx.save();
          const normalizedDatePos = normalizePosition(
            datePosition.x,
            datePosition.y,
            "date"
          );
          ctx.font = `${dateSize}px Arial`;
          ctx.fillStyle = fontColor;
          ctx.fillText(
            new Date().toLocaleDateString(),
            normalizedDatePos.x,
            normalizedDatePos.y
          );
          ctx.restore();

          canvas.toBlob(
            async (blob) => {
              if (blob) {
                const formData = new FormData();
                formData.append("tenantId", id || "");
                formData.append(
                  "certificateId",
                  selectedCertificateId != null ? String(selectedCertificateId) : ""
                );
                formData.append("files", blob, "certificate.png");
                try {
                  const response: { status?: boolean } =
                    await assignCertificateToTenant(formData);
                  if (response.status) {
                    toast.success(
                      t("assign_certificate.certificate_saved_successfully")
                    );
                  } else {
                    toast.error(t("assign_certificate.certificate_saved_failed"));
                  }
                } catch {
                  toast.error(t("assign_certificate.certificate_saved_failed"));
                } finally {
                  resolve();
                }
              } else {
                toast.error(t("assign_certificate.no_file_uploaded"));
                resolve();
              }
            },
            "image/png"
          );
        })
        .catch((error) => {
          console.error("Error drawing image:", error);
          toast.error(t("assign_certificate.certificate_saved_failed"));
          resolve();
        });
    });
  };

  const certInitial = (name: string) => {
    const trimmed = name.trim();
    return trimmed ? trimmed.slice(0, 2) : "?";
  };

  return (
    <div className="root assign-cert-page">
      <Breadcrumb className="assign-cert-breadcrumb">
        <BreadcrumbItem>{t("institution.institutions")}</BreadcrumbItem>
        <span className="mx-2">/</span>
        <BreadcrumbItem active className="text-orange">
          {t("assign_certificate.page_title")}
        </BreadcrumbItem>
      </Breadcrumb>

      <div className="assign-cert-page-head">
        <h4>{t("assign_certificate.page_title")}</h4>
        <p className="assign-cert-subtitle">{t("assign_certificate.page_subtitle")}</p>
      </div>

      <div className="assign-cert-shell">
        <Row>
          <Col md="12">
            <Card className="assign-cert-card mb-3">
              <CardHeader>
                <h5>{t("assign_certificate.select_certificate")}</h5>
              </CardHeader>
              <CardBody>
                {certificates.length === 0 ? (
                  <div className="assign-cert-empty">
                    {t("assign_certificate.no_templates")}
                  </div>
                ) : (
                  <div className="assign-cert-picker" role="list">
                    {certificates.map((cert) => (
                      <button
                        key={cert.id}
                        type="button"
                        role="listitem"
                        className={`assign-cert-chip ${
                          selectedCertificateId === cert.id ? "is-active" : ""
                        }`}
                        onClick={() => handleCertificateSelect(cert.src, cert.id)}
                      >
                        <span className="assign-cert-chip-icon" aria-hidden>
                          {certInitial(cert.name)}
                        </span>
                        <span className="assign-cert-chip-name">{cert.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg="8" md="12">
            {selectedCertificate ? (
              <Card className="assign-cert-card">
                <CardHeader>
                  <h5>{t("assign_certificate.sign_certificate")}</h5>
                </CardHeader>
                <CardBody>
                  {uploadedImage && (
                    <div
                      ref={previewWrapRef}
                      className="assign-cert-preview-wrap"
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <img
                        src={uploadedImage}
                        alt=""
                        onLoad={(e) => {
                          const img = e.target as HTMLImageElement;
                          setImageDimensions({
                            width: img.naturalWidth,
                            height: img.naturalHeight,
                          });
                          setDisplayedImageDimensions({
                            width: img.clientWidth,
                            height: img.clientHeight,
                          });
                        }}
                      />

                      {signatureDataUrl && (
                        <div
                          style={{
                            position: "absolute",
                            top: signaturePosition.y,
                            left: signaturePosition.x,
                            transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                            width: `${signatureSize}px`,
                            height: "auto",
                            border: "1px dashed rgba(26, 179, 148, 0.85)",
                            borderRadius: 4,
                            cursor: "move",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                          }}
                          draggable
                          onDragStart={handleDragStart}
                          onDrag={(e) => handleDrag(e, "signature")}
                          onDragEnd={handleDragEnd}
                        >
                          <img
                            src={signatureDataUrl}
                            alt=""
                            style={{ width: "100%", height: "auto", display: "block" }}
                          />
                        </div>
                      )}

                      <div
                        style={{
                          position: "absolute",
                          top: datePosition.y,
                          left: datePosition.x + 60,
                          cursor: "move",
                          fontSize: `${dateSize}px`,
                          color: fontColor,
                          textShadow: "0 0 4px rgba(255,255,255,0.9)",
                          fontWeight: 600,
                        }}
                        draggable
                        onDragStart={handleDragStart}
                        onDrag={(e) => handleDrag(e, "date")}
                        onDragEnd={handleDragEnd}
                      >
                        {new Date().toLocaleDateString()}
                      </div>
                    </div>
                  )}

                  <p className="assign-cert-hint">{t("assign_certificate.hint_drag")}</p>

                  <div className="assign-cert-pad-wrap">
                    <label htmlFor="assign-cert-signature-pad">
                      {t("assign_certificate.draw_signature")}
                    </label>
                    <canvas
                      id="assign-cert-signature-pad"
                      ref={signatureCanvasRef}
                      width={420}
                      height={120}
                      className="assign-cert-signature-canvas"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                    >
                      Your browser does not support the canvas element.
                    </canvas>
                  </div>
                </CardBody>
                <CardFooter>
                  <button
                    type="button"
                    className="btn-success-app rounded"
                    onClick={saveSignature}
                  >
                    {t("assign_certificate.save_signature")}
                  </button>
                  <button
                    type="button"
                    className="btn-default-app rounded"
                    onClick={clearCanvas}
                  >
                    {t("assign_certificate.clear_signature")}
                  </button>
                </CardFooter>
              </Card>
            ) : (
              <div className="assign-cert-placeholder">
                {t("assign_certificate.pick_template_first")}
              </div>
            )}
          </Col>

          <Col lg="4" md="12" className="mt-3 mt-lg-0">
            {selectedCertificate ? (
              <Card className="assign-cert-card">
                <CardHeader>
                  <h5>{t("assign_certificate.signature_controls")}</h5>
                </CardHeader>
                <CardBody className="assign-cert-controls">
                  <Form>
                    <FormGroup>
                      <Label>{t("assign_certificate.resize_signature")}</Label>
                      <Input
                        type="range"
                        min="50"
                        max="200"
                        disabled={!uploadedImage || !signatureDataUrl}
                        value={signatureSize}
                        onChange={(e) => setSignatureSize(Number(e.target.value))}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>{t("assign_certificate.rotate_signature")}</Label>
                      <Input
                        type="range"
                        min="0"
                        max="360"
                        disabled={!signatureDataUrl || !uploadedImage}
                        value={rotation}
                        onChange={(e) => setRotation(Number(e.target.value))}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>{t("assign_certificate.font_size")}</Label>
                      <Input
                        type="range"
                        min="10"
                        max="50"
                        value={fontSize}
                        disabled={!uploadedImage}
                        onChange={(e) => setFontSize(Number(e.target.value))}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>{t("assign_certificate.font_color")}</Label>
                      <Input
                        type="color"
                        value={fontColor}
                        disabled={!uploadedImage}
                        onChange={(e) => setFontColor(e.target.value)}
                      />
                    </FormGroup>
                    <FormGroup className="mb-0">
                      <Label>{t("assign_certificate.date_size")}</Label>
                      <Input
                        type="range"
                        min="10"
                        max="50"
                        value={dateSize}
                        disabled={!uploadedImage}
                        onChange={(e) => setDateSize(Number(e.target.value))}
                      />
                    </FormGroup>
                  </Form>
                </CardBody>
                <CardFooter>
                  <LoadingButton
                    onClick={saveImageWithSignatureAsync}
                    disabled={!uploadedImage || !signatureDataUrl}
                    className={`${
                      !uploadedImage || !signatureDataUrl
                        ? "submit-button-disables"
                        : "submit-button "
                    } rounded w-100`}
                    variant="success"
                    loadingText={String(t("assign_certificate.saving"))}
                  >
                    {t("assign_certificate.save_final_image")}
                  </LoadingButton>
                </CardFooter>
              </Card>
            ) : null}
          </Col>
        </Row>
      </div>

      <canvas ref={canvasRef} style={{ display: "none" }} aria-hidden />
    </div>
  );
}
